import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { Observable } from 'rxjs';
import { BaseApiResponse } from '../../models/api/api.model';
import {
  GetAvatarResponse,
  UploadAvatarResponse,
  User,
} from '../../models/user/user.model';
import { environment } from '../../../../environments/environment';
import { CurrentUserService } from '../auth/current-user.service';
import {
  UpdateUserRequest,
  GetUserListRequest,
  GetUserListResponse,
  UserListData,
} from '../../models/user/user.model';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private apiService: ApiService,
    private currentUserService: CurrentUserService,
  ) {}

  private apiURL = `${environment.apiLocations.identity}/User`;

  private getUserByIdURL = `${this.apiURL}/getUserById`;
  private updateUserProfileURL = `${this.apiURL}/updateProfile`;
  private updateUserURL = `${this.apiURL}/updateUser`;
  private createUserURL = `${this.apiURL}/createUser`;
  private readonly getUserListURL = `${environment.apiLocations.identity}/user/getListUser`;
  private readonly deleteUserURL = `${environment.apiLocations.identity}/user/deleteUser`;
  private readonly uploadAvatarURL = `${environment.apiLocations.identity}/user/upload-avatar`;
  private readonly deleteAvatarURL = `${environment.apiLocations.identity}/user/delete-avatar`;
  private readonly getAvatarURL = `${environment.apiLocations.identity}/user/getAvatarByUser`;

  /** USER LIST */
  getUserList(request: GetUserListRequest): Observable<GetUserListResponse> {
    let url = `${this.getUserListURL}?PageIndex=${request.index}&PageSize=${request.size}`;

    if (request.Name) {
      url += `&Name=${encodeURIComponent(request.Name)}`;
    }

    if (request.RoleId) {
      url += `&RoleId=${request.RoleId}`;
    }

    if (request.RoleIds && request.RoleIds.length > 0) {
      request.RoleIds.forEach((roleId) => {
        url += `&RoleIds=${roleId}`;
      });
    }
    if (request.OrganizationIds && request.OrganizationIds.length > 0) {
      request.OrganizationIds.forEach((id) => {
        url += `&OrganizationIds=${id}`;
      });
    }
    if (request.FacilityIds && request.FacilityIds.length > 0) {
      request.FacilityIds.forEach((id) => {
        url += `&FacilityIds=${id}`;
      });
    }

    if (request.Email) {
      url += `&Email=${encodeURIComponent(request.Email)}`;
    }

    if (request.OrganizationId) {
      url += `&OrganizationId=${encodeURIComponent(request.OrganizationId)}`;
    }

    if (request.FacilityId) {
      url += `&FacilityId=${encodeURIComponent(request.FacilityId)}`;
    }

    if (request.UserId) {
      url += `&UserId=${encodeURIComponent(request.UserId)}`;
    }

    if (request.SortBy) {
      url += `&SortBy=${request.SortBy}`;
    }

    if (request.SortDirection) {
      url += `&SortDirection=${request.SortDirection}`;
    }
    return this.apiService.get<UserListData>(url);
  }

  getListUserByRoleType(
    roleType: number,
    organizationId?: string,
  ): Observable<BaseApiResponse<any>> {
    let url = `${this.apiURL}/GetListUserByRoleType?RoleType=${roleType}`;
    if (organizationId) {
      url += `&OrganizationId=${organizationId}`;
    }
    return this.apiService.get<BaseApiResponse<any>>(url);
  }

  /** DELETE USER */
  deleteUser(id: string): Observable<any> {
    const body = { id };

    return new Observable((observer) => {
      this.apiService.post(this.deleteUserURL, body).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => observer.error(err),
      });
    });
  }

  getUserById(userId: string): Observable<BaseApiResponse<any>> {
    return new Observable((observer) => {
      this.apiService.get<any>(`${this.getUserByIdURL}/${userId}`).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  getCurrentUserProfile(): Observable<BaseApiResponse<User>> {
    const session = this.currentUserService.GetSession();

    const userId = session?.id;

    if (!userId) {
      console.error('Not Found');
    }

    return new Observable((observer) => {
      this.apiService.get<User>(`${this.getUserByIdURL}/${userId}`).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }
  updateUser(request: UpdateUserRequest): Observable<BaseApiResponse<any>> {
    return new Observable((observer) => {
      this.apiService.post(this.updateUserURL, request).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  updateUserProfile(request: {
    name: string;
    surName: string;
    phoneNumber: string;
    email: string;
  }): Observable<BaseApiResponse<any>> {
    return new Observable((observer) => {
      this.apiService.post(this.updateUserProfileURL, request).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  /** CREATE USER */
  createUser(request: any): Observable<BaseApiResponse<any>> {
    return new Observable((observer) => {
      this.apiService.post(this.createUserURL, request).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  getAvatar(userId: string): Observable<BaseApiResponse<GetAvatarResponse>> {
    return new Observable((observer) => {
      this.apiService
        .get<GetAvatarResponse>(`${this.getAvatarURL}/${userId}`)
        .subscribe({
          next: (res) => {
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
          },
        });
    });
  }

  uploadAvatar(
    form: FormData,
  ): Observable<BaseApiResponse<UploadAvatarResponse>> {
    return new Observable((observer) => {
      this.apiService.post(this.uploadAvatarURL, form).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  deleteAvatar(userId: string): Observable<BaseApiResponse<any>> {
    return new Observable((observer) => {
      this.apiService.post(this.deleteAvatarURL, { userId }).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => observer.error(err),
      });
    });
  }
}
